import { useState, useEffect } from 'react';
import { getMarketCrashList, addMarketCrashEntry, updateMarketCrashEntry, deleteMarketCrashEntry, getClients } from '../lib/api';
import { formatApiError, formatCurrency, getClientDisplayName } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash, TrendDown } from '@phosphor-icons/react';

export default function MarketCrashPage() {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [addForm, setAddForm] = useState({ client_id: '', amount: '' });

  const fetchData = async () => {
    try {
      const [entriesRes, clientsRes] = await Promise.all([
        getMarketCrashList(),
        getClients()
      ]);
      setEntries(entriesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleAdd = async () => {
    if (!addForm.client_id) { toast.error('Please select a client'); return; }
    setSaving(true);
    try {
      await addMarketCrashEntry({
        client_id: addForm.client_id,
        amount: parseFloat(addForm.amount) || 0
      });
      toast.success('Client added to market crash list');
      setShowAddModal(false);
      setAddForm({ client_id: '', amount: '' });
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingEntry) return;
    setSaving(true);
    try {
      await updateMarketCrashEntry(editingEntry.id, {
        amount: parseFloat(editingEntry.amount) || 0,
        notes: editingEntry.notes || ''
      });
      toast.success('Amount updated');
      setEditingEntry(null);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteMarketCrashEntry(deleteId);
      toast.success('Removed from list');
      setDeleteId(null);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const totalAmount = entries.reduce((sum, e) => sum + (e.amount || 0), 0);
  const existingClientIds = new Set(entries.map(e => e.client_id));

  if (loading) return (
    <div className="p-6 space-y-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-[400px]" />
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="market-crash-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Market Crash Deploy List
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Track lumpsum deployment amounts for clients</p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm"
          data-testid="add-crash-entry-btn"
        >
          <Plus size={16} className="mr-2" /> Add Client
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-zinc-200 rounded-sm">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Total Clients</p>
            <p className="text-3xl font-bold mt-1 tabular-nums">{entries.length}</p>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 rounded-sm">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Total Deployment Amount</p>
            <p className="text-3xl font-bold mt-1 tabular-nums text-[#002FA7]">{formatCurrency(totalAmount)}</p>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 rounded-sm">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Ready to Deploy</p>
            <p className="text-3xl font-bold mt-1 tabular-nums text-emerald-600">{entries.filter(e => e.amount > 0).length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-zinc-200 rounded-sm">
        <CardContent className="p-0">
          {entries.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">
              <TrendDown size={40} className="mx-auto mb-3 text-zinc-300" />
              <p>No clients in the deployment list yet</p>
              <Button variant="link" onClick={() => setShowAddModal(true)} className="text-[#002FA7] mt-2">
                Add your first client
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">#</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Client Name</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Phone</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold text-right">Amount to Deploy</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry, idx) => (
                  <TableRow key={entry.id} className="hover:bg-zinc-50" data-testid={`crash-entry-${entry.id}`}>
                    <TableCell className="text-zinc-400 text-sm">{idx + 1}</TableCell>
                    <TableCell className="font-medium">{entry.client_name || 'Unknown'}</TableCell>
                    <TableCell className="text-sm text-zinc-600">{entry.client_phone || '—'}</TableCell>
                    <TableCell className="text-right">
                      <span className={`text-sm font-semibold tabular-nums ${entry.amount > 0 ? 'text-emerald-600' : 'text-zinc-400'}`}>
                        {entry.amount > 0 ? formatCurrency(entry.amount) : '₹0'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          variant="ghost" size="sm" className="h-8 w-8 p-0"
                          onClick={() => setEditingEntry({ ...entry })}
                          data-testid={`edit-crash-${entry.id}`}
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          onClick={() => setDeleteId(entry.id)}
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Client to Deploy List</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Client <span className="text-red-500">*</span></Label>
              <SearchableSelect
                value={addForm.client_id}
                onValueChange={(v) => setAddForm(p => ({ ...p, client_id: v }))}
                options={clients.filter(c => !existingClientIds.has(c.id)).map(c => ({ value: c.id, label: getClientDisplayName(c) }))}
                placeholder="Select client"
                searchPlaceholder="Search clients..."
                data-testid="crash-client-select"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Amount to Deploy</Label>
              <Input
                type="number"
                min="0"
                value={addForm.amount}
                onChange={(e) => setAddForm(p => ({ ...p, amount: e.target.value }))}
                placeholder="0"
                className="rounded-sm"
                data-testid="crash-amount-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={saving} className="bg-[#002FA7] text-white" data-testid="crash-add-submit">
              {saving ? 'Adding...' : 'Add to List'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={!!editingEntry} onOpenChange={() => setEditingEntry(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Amount — {editingEntry?.client_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Amount to Deploy</Label>
              <Input
                type="number"
                min="0"
                value={editingEntry?.amount || ''}
                onChange={(e) => setEditingEntry(p => p ? { ...p, amount: e.target.value } : null)}
                className="rounded-sm"
                data-testid="crash-edit-amount"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingEntry(null)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={saving} className="bg-[#002FA7] text-white" data-testid="crash-edit-submit">
              {saving ? 'Updating...' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from list?</AlertDialogTitle>
            <AlertDialogDescription>This will remove the client from the market crash deploy list.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 text-white hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
